import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Typography,
  Alert,
} from '@mui/material';
import { alpha, useTheme, Theme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { getMoldsByCustomerId } from '../services/mold.service';
import { Mold, MoldStatusMap, MoldStatus } from '../types';

const buildSurface = (theme: Theme) => ({
  borderRadius: 3,
  border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
  backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
    theme.palette.primary.dark,
    0.24,
  )})`,
  boxShadow: `0 20px 38px ${alpha(theme.palette.primary.dark, 0.28)}`,
  backdropFilter: 'blur(14px)',
});

const CustomerDashboard: React.FC = () => {
  const theme = useTheme<Theme>();
  const { user } = useAuth();
  const [molds, setMolds] = useState<Mold[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMolds();
  }, [user]);

  const loadMolds = async () => {
    if (!user?.customerId) {
      setError('거래처 정보가 없습니다. 관리자에게 문의하세요.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getMoldsByCustomerId(user.customerId);
      setMolds(data);
      setError('');
    } catch (err) {
      setError('금형 정보를 불러올 수 없습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getLifetimePercentage = (mold: Mold) => {
    return (mold.currentShots / mold.designLife) * 100;
  };

  const getLifetimeColor = (percentage: number) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 80) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography>금형 정보를 불러오는 중...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          대시보드
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.companyName || '고객사'} 보유 금형 목록
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {molds.length === 0 ? (
        <Card sx={buildSurface(theme)}>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              등록된 금형이 없습니다.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            gap: 3,
          }}
        >
          {molds.map((mold) => {
            const lifetimePercentage = getLifetimePercentage(mold);
            const lifetimeColor = getLifetimeColor(lifetimePercentage);

            return (
              <Box key={mold.id}>
                <Card sx={buildSurface(theme)}>
                  <CardContent>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {mold.moldNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {mold.moldName}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          상태
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={MoldStatusMap[mold.status as MoldStatus]}
                            size="small"
                            color={mold.status === 'in-use' ? 'primary' : 'default'}
                          />
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          제품명
                        </Typography>
                        <Typography variant="body2">{mold.productName}</Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          보관위치
                        </Typography>
                        <Typography variant="body2">{mold.location}</Typography>
                      </Box>

                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            수명
                          </Typography>
                          <Typography variant="caption" color={`${lifetimeColor}.main`} sx={{ fontWeight: 600 }}>
                            {lifetimePercentage.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(lifetimePercentage, 100)}
                          color={lifetimeColor}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {mold.currentShots.toLocaleString()} / {mold.designLife.toLocaleString()} 샷
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default CustomerDashboard;
